// Amounts in words for documents (08-DOCUMENTS: totals "each also in words (EN and AR)").
// English: "Thirty thousand seven hundred and five Saudi riyals and 50 halalas".
// Arabic: the invoice form used in the region, "فقط ثلاثون ألفاً ... ريال سعودي لا غير", which
// keeps the currency name singular whatever the number (no gender or plural agreement needed).

export interface CurrencyWords {
  en: [major: string, minor: string];
  ar: [major: string, minor: string];
}

export const CURRENCY_WORDS: Record<string, CurrencyWords> = {
  SAR: { en: ['Saudi riyals', 'halalas'], ar: ['ريال سعودي', 'هللة'] },
  AED: { en: ['UAE dirhams', 'fils'], ar: ['درهم إماراتي', 'فلس'] },
  EGP: { en: ['Egyptian pounds', 'piastres'], ar: ['جنيه مصري', 'قرش'] },
  USD: { en: ['US dollars', 'cents'], ar: ['دولار أمريكي', 'سنت'] },
  EUR: { en: ['euros', 'cents'], ar: ['يورو', 'سنت'] },
};

const ONES = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const SCALES_EN = ['', 'thousand', 'million', 'billion'];

function enBelow1000(n: number): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  const parts: string[] = [];
  if (h) parts.push(`${ONES[h]} hundred`);
  if (r) {
    const rest =
      r < 20 ? ONES[r] : `${TENS[Math.floor(r / 10)]}${r % 10 ? `-${ONES[r % 10]}` : ''}`;
    parts.push(h ? `and ${rest}` : (rest ?? ''));
  }
  return parts.join(' ');
}

/** Whole numbers up to 999,999,999,999 in British English ("one thousand and five"). */
export function numberToEnglish(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n >= 1e12) throw new RangeError('Out of range');
  if (n === 0) return 'zero';
  const groups: number[] = [];
  for (let v = n; v > 0; v = Math.floor(v / 1000)) groups.push(v % 1000);
  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i] ?? 0;
    if (!g) continue;
    const words = enBelow1000(g);
    // "one thousand and five": a last group under 100 after a higher group takes "and".
    const and = i === 0 && g < 100 && parts.length ? 'and ' : '';
    parts.push(`${and}${words}${SCALES_EN[i] ? ` ${SCALES_EN[i]}` : ''}`);
  }
  return parts.join(' ');
}

const AR_ONES = [
  '',
  'واحد',
  'اثنان',
  'ثلاثة',
  'أربعة',
  'خمسة',
  'ستة',
  'سبعة',
  'ثمانية',
  'تسعة',
  'عشرة',
  'أحد عشر',
  'اثنا عشر',
  'ثلاثة عشر',
  'أربعة عشر',
  'خمسة عشر',
  'ستة عشر',
  'سبعة عشر',
  'ثمانية عشر',
  'تسعة عشر',
];
const AR_TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const AR_HUNDREDS = [
  '',
  'مائة',
  'مائتان',
  'ثلاثمائة',
  'أربعمائة',
  'خمسمائة',
  'ستمائة',
  'سبعمائة',
  'ثمانمائة',
  'تسعمائة',
];
// [one, two, 3–10 (plural), 11+ (singular, accusative)]
const AR_SCALES: [string, string, string, string][] = [
  ['', '', '', ''],
  ['ألف', 'ألفان', 'آلاف', 'ألفاً'],
  ['مليون', 'مليونان', 'ملايين', 'مليوناً'],
  ['مليار', 'ملياران', 'مليارات', 'ملياراً'],
];

function arBelow1000(n: number): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  const parts: string[] = [];
  if (h) parts.push(AR_HUNDREDS[h] ?? '');
  if (r) {
    parts.push(
      r < 20
        ? (AR_ONES[r] ?? '')
        : r % 10
          ? `${AR_ONES[r % 10]} و${AR_TENS[Math.floor(r / 10)]}`
          : (AR_TENS[Math.floor(r / 10)] ?? ''),
    );
  }
  return parts.join(' و');
}

/** Whole numbers up to 999,999,999,999 in Arabic (masculine counting form). */
export function numberToArabic(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n >= 1e12) throw new RangeError('Out of range');
  if (n === 0) return 'صفر';
  const groups: number[] = [];
  for (let v = n; v > 0; v = Math.floor(v / 1000)) groups.push(v % 1000);
  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i] ?? 0;
    if (!g) continue;
    const scale = AR_SCALES[i] ?? ['', '', '', ''];
    if (i === 0) parts.push(arBelow1000(g));
    else if (g === 1) parts.push(scale[0]);
    else if (g === 2) parts.push(scale[1]);
    else if (g <= 10) parts.push(`${arBelow1000(g)} ${scale[2]}`);
    else parts.push(`${arBelow1000(g)} ${scale[3]}`);
  }
  return parts.join(' و');
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** An amount in minor units, in words: { en, ar }. */
export function amountInWords(minor: number, currency: string): { en: string; ar: string } {
  if (!Number.isInteger(minor) || minor < 0) throw new RangeError('Out of range');
  const words = CURRENCY_WORDS[currency] ?? {
    en: [currency, 'cents'],
    ar: [currency, 'سنت'],
  };
  const major = Math.floor(minor / 100);
  const cents = minor % 100;
  const en = `${cap(numberToEnglish(major))} ${words.en[0]}${cents ? ` and ${cents} ${words.en[1]}` : ''}`;
  const ar = `فقط ${numberToArabic(major)} ${words.ar[0]}${cents ? ` و${numberToArabic(cents)} ${words.ar[1]}` : ''} لا غير`;
  return { en, ar };
}
